"use client";
import React from "react";
import { Box, Container, Typography, Chip } from "@mui/material";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import "../app/globals.css";
import { IMAGES } from "../constants/images";

const GetgeekBeforeAfter = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundImage: `url('${IMAGES.BEFORE_AFTER_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 4, sm: 5, md: 6, lg: 8 },
        height: { xs: "auto", md: "auto", lg: "auto", xl: "107vh" },
        minHeight: { xs: "auto", md: "auto" },
        "@media (min-width: 1536px) and (max-width: 1700px)": {
          height: "127vh",
        },
        "@media (min-width: 1701px) and (max-width: 1800px)": {
          height: "120vh",
        },
        "@media (min-width: 1536px) and (max-width: 1700px)": {
          height: "140vh",
        }, 
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            gap: { xs: 3, sm: 4, md: 6, lg: 8 },
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            ml: { xs: "-2%", sm: "-2%", md: "0" },
          }}
        >
          {/* Left Side - Before/After Images (hidden on xs/sm) */}
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: "100%", md: "380px" },
              display: { xs: "none", sm: "none", md: "flex" },
              flexDirection: "column",
              alignItems: isArabic ? "flex-end" : "flex-start",
            }}
          >
            <Box sx={{ mb: 2, position: "relative", width: "100%" }}>
              <Image
                src={IMAGES.BEFORE_IMAGE}
                alt="Traditional recruiting software challenges"
                width={800}
                height={420}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "420px",
                  objectFit: "cover",
                  borderRadius: "12px",
                }}
              />
            </Box>
            <Box sx={{ position: "relative", width: "100%" }}>
              <Image
                src={IMAGES.AFTER_IMAGE}
                alt="Best recruitment automation software showing AI recruiting tool advantages"
                width={800}
                height={420}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "420px",
                  objectFit: "cover",
                  borderRadius: "12px",
                }}
              />
            </Box>
          </Box>

          {/* Right Side - Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: "100%", md: "350px" },
              mt: { xs: 2, sm: 3, md: "4%" },
              textAlign: isArabic ? "right" : "left",
              px: { xs: 2, sm: 3, md: 0 },
            }}
          >
            {/* Chip */}
            <Chip
              icon={
                <Image
                  src={IMAGES.WARNING_ICON}
                  alt="Warning"
                  width={22}
                  height={22}
                  style={{ objectFit: "contain" }}
                />
              }
              label={t("getgeekBeforeAfter.chip")}
              sx={{
                bgcolor: "#fee7d5",
                color: "#FF9B18",
                fontWeight: 600,
                fontFamily: "var(--font-outfit)",
                borderRadius: "9999px",
                px: { xs: 1.5, sm: 2 },
                py: { xs: 2.5, sm: 3 },
                fontSize: { xs: "12px", sm: "14px", md: "15px", lg: "16px" },
                "& .MuiChip-icon": {
                  marginLeft: isArabic ? "0px" : "4px",
                  marginRight: isArabic ? "4px" : "2px",
                },
              }}
            />

            {/* Heading */}
            <Typography
              variant="h3"
              sx={{
                fontSize: {
                  xs: "22px",
                  sm: "26px",
                  md: "30px",
                  lg: "30px",
                  xl: "42px",
                },
                fontWeight: 700,
                lineHeight: 1.3,
                mt: { xs: 3, sm: 3.5, md: 4 },
                mb: { xs: 2, sm: 2.5, md: 3 },
                color: "#020617",
                maxWidth: { xs: "100%", lg: "600px" },
                textAlign: isArabic ? "right" : "left",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("getgeekBeforeAfter.title1")}
              <Box
                component="span"
                sx={{ color: "#FFB200", ml: { xs: "4px", lg: "1%" } }}
              >
                {t("getgeekBeforeAfter.titleHighlight1")}
              </Box>
              {t("getgeekBeforeAfter.title2")}
              <Box
                component="span"
                sx={{ color: "#FFB200", ml: { xs: "4px", lg: "1%" } }}
              >
                {t("getgeekBeforeAfter.titleHighlight2")}
              </Box>
            </Typography>

            {/* Para 1 */}
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: "#02061799",
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontFamily: "var(--font-outfit)",
                fontSize: {
                  xs: "14px",
                  sm: "15px",
                  md: "15px",
                  lg: "15px",
                  xl: "19px",
                },
                fontWeight: 400,
                maxWidth: { xs: "100%", lg: "650px" },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("getgeekBeforeAfter.para1")}
            </Typography>

            {/* Para 2 */}
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: "#02061799",
                pt: { xs: "2%", sm: "2.5%", md: "3%" },
                mb: { xs: 2, sm: 2.5, md: 0 },
                fontFamily: "var(--font-outfit)",
                fontSize: {
                  xs: "14px",
                  sm: "15px",
                  md: "15px",
                  lg: "15px",
                  xl: "19px",
                },
                fontWeight: 400,
                maxWidth: { xs: "100%", lg: "650px" },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("getgeekBeforeAfter.para2")}
            </Typography>

            {/* Para 3 */}
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: "#02061799",
                pt: { xs: "2%", sm: "2.5%", md: "3%" },
                pb: { xs: 2, sm: 3, md: 0 },
                fontFamily: "var(--font-outfit)",
                fontSize: {
                  xs: "14px",
                  sm: "15px",
                  md: "15px",
                  lg: "15px",
                  xl: "19px",
                },
                fontWeight: 400,
                maxWidth: { xs: "100%", lg: "650px" },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("getgeekBeforeAfter.para3")}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default GetgeekBeforeAfter;
